// src/handlers/kegHandlers.js
// ============================================================
// KEG MANAGEMENT HANDLERS
// ============================================================
// Handles all keg-related operations:
// - Creating new kegs
// - Editing keg details
// - Deleting kegs
// - Managing keg status (tap, cleaning)
// - QR code generation
//
// All operations follow the pattern:
// 1. Prepare data from form state
// 2. Call SupabaseService function
// 3. Update local state on success
// 4. Show error on failure
// 5. Close modal and reset form
// ============================================================

import { generateUUID } from '../kegops-utils';
import * as SupabaseService from '../supabaseService';
import { normalizeString } from '../utils';
import * as UsageService from '../services/usageService';

export const createKegHandlers = (state) => {
  // Destructure only the state this handler needs
  // This makes dependencies explicit and reduces confusion
  const {
    kegForm, setKegForm,
    kegs, setKegs,
    setShowKegModal,
    setEditingKeg,
    setPendingTapStatus,
    setShowTapSelectionModal,
    tapSelectionKeg, setTapSelectionKeg,
    selectedTapLocation, setSelectedTapLocation,
    selectedTapSublocation, setSelectedTapSublocation,
    selectedTapName, setSelectedTapName,
    setShowFeedbackModal,
    setFeedbackRating,
    setFeedbackNotes,
    setFeedbackName
  } = state;

  /**
   * Creates a new keg with default values
   * @async
   * @description Validates form data, checks usage limits, creates keg object with UUID,
   * sends to Supabase, updates local state
   */
  const handleAddKeg = async () => {
    // Check usage limits
    const selectedBrewery = state.breweries?.find(b => b.id === state.selectedBreweryId);
    if (selectedBrewery) {
      const canAdd = UsageService.canAddKeg(selectedBrewery.id, selectedBrewery.plan, kegs);
      if (!canAdd.allowed) {
        alert(`⚠️ Keg Limit Reached\n\n${canAdd.message}\n\nYou have ${canAdd.current}/${canAdd.limit} kegs.`);
        return;
      }
    }

    const keg = {
      uuid: generateUUID(),
      kegNumber: parseInt(kegForm.kegNumber),
      name: kegForm.name,
      owner: kegForm.owner,
      tapStatus: "Empty",
      cleaningStatus: "Dirty",
      location: kegForm.location,
      sublocation: kegForm.sublocation,
      batchCount: 0,
      isEmpty: true,
      currentBeer: null,
      brewery_id: state.selectedBreweryId,
      kegDetails: {
        type: kegForm.type,
        volume: parseFloat(kegForm.volume),
        features: kegForm.features || [],
        notes: kegForm.notes || ''
      },
      weights: {
        tare: kegForm.tareWeight ? parseFloat(kegForm.tareWeight) : null
      },
      maintenanceRecords: [],
      locationHistory: kegForm.location ? [
        { location: kegForm.location, sublocation: kegForm.sublocation,
          date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) }
      ] : [],
      beerHistory: []
    };

    const result = await SupabaseService.addKeg(keg, state.selectedBreweryId);
    
    if (result.success) {
      setKegs(prev => [...prev, result.data]);
      setShowKegModal(false);
      setKegForm({
        kegNumber: '', name: '', owner: 'Brewer Born', type: 'Ball Lock', volume: '',
        tareWeight: '', features: [], notes: '', location: '', sublocation: ''
      });
    } else {
      alert('Error adding keg: ' + result.error);
    }
  };

  const handleSaveKeg = async (kegId) => {
    // Build update object with only changed fields
    // This prevents unnecessary data transmission
    const updates = {
      kegNumber: parseInt(kegForm.kegNumber),
      name: kegForm.name,
      owner: kegForm.owner,
      kegDetails: {
        type: kegForm.type,
        volume: parseFloat(kegForm.volume),
        features: kegForm.features,
        notes: kegForm.notes
      },
      weights: {
        tare: kegForm.tareWeight ? parseFloat(kegForm.tareWeight) : null
      }
    };

    const result = await SupabaseService.updateKeg(kegId, updates);
    
    if (result.success) {
      const kegsResult = await SupabaseService.fetchKegs();
      if (kegsResult.success !== false) {
        setKegs(kegsResult);
      }
      setShowKegModal(false);
      setEditingKeg(null);
    } else {
      alert('Error updating keg: ' + result.error);
    }
  };

  const handleDeleteKeg = async (kegId) => {
    // Always confirm destructive actions before proceeding
    // This prevents accidental deletions
    if (!window.confirm('Are you sure you want to delete this keg? This cannot be undone.')) {
      return;
    }

    const result = await SupabaseService.deleteKeg(kegId);
    
    if (result.success) {
      setKegs(prev => prev.filter(k => k.id !== kegId));
    } else {
      alert('Error deleting keg: ' + result.error);
    }
  };

  const handleTapStatusChange = async (kegId, newStatus) => {
    // If switching to 'On Tap', open the tap selection modal
    if (normalizeString(newStatus) === 'on tap') {
      const targetKeg = kegs.find(k => k.id === kegId);
      if (targetKeg) {
        setTapSelectionKeg(targetKeg);
        setSelectedTapLocation(targetKeg.location || '');
        setSelectedTapSublocation(targetKeg.sublocation || '');
        setSelectedTapName(targetKeg.tapNumber || '');
        setShowTapSelectionModal(true);
      }
      return;
    }

    const result = await SupabaseService.updateKeg(kegId, { tapStatus: newStatus });
    if (result.success) {
      setKegs(prev => prev.map(k => k.id === kegId ? { ...k, tapStatus: newStatus } : k));
    } else {
      alert('Error updating tap status: ' + result.error);
    }
  };

  // handleSetTapNumber removed; tap selection now handled by handleSaveTapSelection

  /**
   * Saves tap selection when assigning keg to a tap
   * @async
   * @description Checks if the tap is already occupied. If so, prompts the user
   * to kick the existing keg and rate it. If not, assigns the keg to the tap.
   * @param {string} location - Selected location name
   * @param {string} sublocation - Selected sublocation name (if any)
   * @param {string} tapName - Selected/entered tap name
   */
  const handleSaveTapSelection = async (location, sublocation, tapName) => {
    if (!tapSelectionKeg) return;

    // Check if another keg is already on this tap
    const existingKeg = kegs.find(k => 
      k.id !== tapSelectionKeg.id &&  // Don't check against self
      normalizeString(k.tapStatus) === 'on tap' &&
      k.tapNumber === tapName &&
      k.location === location &&
      k.sublocation === sublocation
    );

    // If a keg is already on this tap, kick it first
    if (existingKeg) {
      // Close the tap selection modal
      setShowTapSelectionModal(false);
      
      // Store the new keg's tap info temporarily
      setTapSelectionKeg(prevKeg => ({
        ...prevKeg,
        _pendingLocation: location,
        _pendingSublocation: sublocation,
        _pendingTapName: tapName
      }));
      
      // Open the feedback modal for the existing keg to kick it
      setShowFeedbackModal(true);
      setFeedbackRating(0);
      setFeedbackNotes('');
      setFeedbackName('');
      
      // Mark that we need to assign the new keg after the kick is complete
      setPendingTapStatus({
        kegId: tapSelectionKeg.id,
        location,
        sublocation,
        tapName
      });

      return;
    }

    // No conflict - proceed with assignment
    const updates = {
      tapStatus: 'On Tap',
      location: location,
      sublocation: sublocation,
      tapNumber: tapName
    };

    const result = await SupabaseService.updateKeg(tapSelectionKeg.id, updates);
    if (result.success) {
      setKegs(prev => prev.map(k => 
        k.id === tapSelectionKeg.id ? { ...k, ...updates } : k
      ));
      setShowTapSelectionModal(false);
      setTapSelectionKeg(null);
      setSelectedTapLocation('');
      setSelectedTapSublocation('');
      setSelectedTapName('');
    } else {
      alert('Error assigning keg to tap: ' + result.error);
    }
  };

  /**
   * Moves an existing beer from one tap to another
   * @async
   * @description When a tap is occupied, allows moving the current beer to an
   * empty tap instead of kicking it. Then assigns the new keg to the original tap.
   * @param {object} kegToMove - The keg currently on the occupied tap
   * @param {string} newTapName - The new tap to move the beer to
   */
  const handleMoveBeerToTap = async (kegToMove, newTapName) => {
    if (!tapSelectionKeg || !kegToMove) return;

    // Get the original selected tap info
    const originalLocation = tapSelectionKeg._pendingLocation || selectedTapLocation;
    const originalSublocation = tapSelectionKeg._pendingSublocation || selectedTapSublocation;
    const originalTapName = tapSelectionKeg._pendingTapName || selectedTapName;

    // Step 1: Move the existing beer to the new tap
    const moveResult = await SupabaseService.updateKeg(kegToMove.id, {
      tapNumber: newTapName,
      location: originalLocation,
      sublocation: originalSublocation,
      tapStatus: 'On Tap'
    });

    if (!moveResult.success) {
      alert('Error moving beer to new tap: ' + moveResult.error);
      return;
    }

    // Step 2: Assign the new keg to the original tap
    const assignResult = await SupabaseService.updateKeg(tapSelectionKeg.id, {
      tapStatus: 'On Tap',
      location: originalLocation,
      sublocation: originalSublocation,
      tapNumber: originalTapName
    });

    if (assignResult.success) {
      const kegsResult = await SupabaseService.fetchKegs();
      if (kegsResult.success !== false) {
        setKegs(kegsResult);
      }
      
      // Close the modal and reset state
      setShowTapSelectionModal(false);
      setTapSelectionKeg(null);
      setSelectedTapLocation('');
      setSelectedTapSublocation('');
      setSelectedTapName('');
    } else {
      alert('Error assigning keg to tap: ' + assignResult.error);
    }
  };

  const handleCleaningStatusChange = async (kegId, newStatus) => {
    const result = await SupabaseService.updateKeg(kegId, { cleaningStatus: newStatus });
    
    if (result.success) {
      setKegs(prev => prev.map(k => 
        k.id === kegId ? { ...k, cleaningStatus: newStatus } : k
      ));
    } else {
      alert('Error updating cleaning status: ' + result.error);
    }
  };

  const handleLocationChange = async (keg, newLocation, newSublocation) => {
    if (newLocation && (newLocation !== keg.location || newSublocation !== keg.sublocation)) {
      await SupabaseService.addLocationHistory(keg.id, newLocation, newSublocation);
      
      const result = await SupabaseService.updateKeg(keg.id, {
        location: newLocation,
        sublocation: newSublocation
      });
      
      if (result.success) {
        const kegsResult = await SupabaseService.fetchKegs();
        if (kegsResult.success !== false) {
          setKegs(kegsResult);
        }
      }
    }
  };

  const handleBulkUpdate = async (kegIds, field, value) => {
    const updates = { [field]: value };
    const result = await SupabaseService.bulkUpdateKegs(kegIds, updates);
    
    if (result.success) {
      setKegs(prev => prev.map(k => 
        kegIds.includes(k.id) ? { ...k, [field]: value } : k
      ));
    } else {
      alert('Error updating kegs: ' + result.error);
    }
  };

  const handleOpenKegModal = (keg = null) => {
    if (keg) {
      setEditingKeg(keg);
      setKegForm({
        kegNumber: keg.kegNumber,
        name: keg.name,
        owner: keg.owner,
        type: keg.kegDetails.type,
        volume: keg.kegDetails.volume,
        tareWeight: keg.weights.tare || '',
        features: keg.kegDetails.features || [],
        notes: keg.kegDetails.notes || '',
        location: keg.location || '',
        sublocation: keg.sublocation || ''
      });
      setShowKegModal(true);
    } else {
      setEditingKeg(null);
      setKegForm({
        kegNumber: '', name: '', owner: 'Brewer Born', type: 'Ball Lock', volume: '',
        tareWeight: '', features: [], notes: '', location: '', sublocation: ''
      });
      setShowKegModal(true);
    }
  };

  return {
    handleAddKeg,
    handleSaveKeg,
    handleDeleteKeg,
    handleTapStatusChange,
    handleSaveTapSelection,
    handleMoveBeerToTap,
    handleCleaningStatusChange,
    handleLocationChange,
    handleBulkUpdate,
    handleOpenKegModal
  };
};